import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { TestFilters } from "./types";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { updateOrgStructure } from "../../../../../store/orgStructure/useCases/updateOrgStructure/action";
import { selectOrgStructure } from "../../../../../store/orgStructure/repository/selector";
import { getTestList } from "../../../../../store/test/useCases/getTestList/action";
import { selectTest } from "../../../../../store/test/repository/selector";
import { updateTest } from "../../../../../store/test/useCases/updateTest/action";
import { deleteTest } from "../../../../../store/test/useCases/deleteTest/action";
import { uploadTestReport } from "../../../../../store/test/useCases/uploadTestReport/action";
import toast from "react-hot-toast";
import { tokens } from "../../../../../locales/tokens";
import dayjs from "dayjs";
import { setTestList } from "../../../../../store/test/repository/slice";
import { deleteTestHard } from "../../../../../store/test/useCases/deleteTestHard/action";

export const useTestList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchText, setSearchText] = useState("");
  const [pageAmount, setPageAmount] = useState(0);
  const [updateClick, setUpdateClick] = useState(false);
  const [deleteClick, setDeleteClick] = useState(false);
  const { orgStructure } = useSelector(selectOrgStructure);
  const {
    testList,
    testsCount,
    isLoading,
    deleteTestError,
    updateTestError,
    getTestListError,
  } = useSelector(selectTest);
  const [isProccessTest, setProcessTest] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const findStatusLabel = (value: string) => {
    if (value === "Available") return t(tokens.documents.publish);
    if (value === "Hidden") return t(tokens.documents.notPublish);
    return "";
  };

  const getFiltersFromUrl = (): TestFilters => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    const tab: TestFilters["tab"] =
      (tabParam as TestFilters["tab"]) || "Active";
    return {
      page: Number(params.get("page")) || 1,
      rowsPerPage: Number(params.get("rowsPerPage")) || 30,
      search: params.get("search") || "",
      tab,
      status: params.get("status")
        ? {
            value: params.get("status"),
            label: findStatusLabel(params.get("status") as string),
          }
        : null,
      initiator: params.get("initiator")
        ? {
            value: params.get("initiator"),
            label: params.get("initiatorName") || "",
          }
        : null,
      date: params.get("date") ? Number(params.get("date")) : null,
    };
  };

  const [filters, updateFilters] = useState<TestFilters>(getFiltersFromUrl);

  useEffect(() => {
    setSearchText(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.page) params.set("page", filters.page.toString());
    if (filters.rowsPerPage)
      params.set("rowsPerPage", filters.rowsPerPage.toString());
    if (filters.search) params.set("search", filters.search);
    if (filters.tab) params.set("tab", filters.tab);
    if (filters.status?.value) params.set("status", filters.status.value);
    if (filters.initiator?.value) {
      params.set("initiator", filters.initiator.value);
      if (filters.initiator.label)
        params.set("initiatorName", filters.initiator.label);
    }
    if (filters.date) params.set("date", filters.date.toString());

    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);

  useEffect(() => {
    dispatch(getTestList(combineRequestBody()));
    setProcessTest(true);
    return () => {
      dispatch(setTestList([]));
    };
  }, [filters]);

  useEffect(() => {
    if (testsCount) {
      setPageAmount(Math.ceil(testsCount / filters.rowsPerPage));
    }
  }, [testsCount]);

  useEffect(() => {
    dispatch(updateOrgStructure());
  }, []);

  useEffect(() => {
    if (!isLoading && isProccessTest) {
      if (getTestListError) {
        toast.error(getTestListError);
      }
      setTimeout(() => setProcessTest(false), 400);
    }
  }, [getTestListError, isLoading]);

  useEffect(() => {
    if (!isLoading && updateClick) {
      if (updateTestError) {
        toast.error(updateTestError);
      } else {
        toast.success(t(tokens.test.message.testUpdated));
        dispatch(getTestList(combineRequestBody()));
      }
      setUpdateClick(false);
    }
  }, [updateTestError, isLoading]);

  useEffect(() => {
    if (!isLoading && deleteClick) {
      if (deleteTestError) {
        toast.error(deleteTestError);
      } else {
        filters.tab === "Active"
          ? toast.success(t(tokens.test.message.testRemoved))
          : toast.success(t(tokens.test.message.testRemovedHard));
        dispatch(getTestList(combineRequestBody()));
      }
      setDeleteClick(false);
    }
  }, [deleteTestError, isLoading]);

  const handleSearchTestText = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    debouncedHandleTestSearch(e.target.value);
  };

  const onSearchTest = (value: string) => {
    const search = value || "";
    if (search.length !== 1) {
      updateFilters((prev) => ({ ...prev, search, page: 1 }));
      scrollTopList();
    }
  };

  const { debouncedWrapper: debouncedHandleTestSearch } = useDebounce(
    onSearchTest,
    1000
  );

  const handleTabsChange = (event: ChangeEvent<{}>, tab: string): void => {
    updateFilters(
      (prev): TestFilters => ({
        ...prev,
        page: 1,
        rowsPerPage: 30,
        search: "",
        tab: "Active" as const,
        status: null,
        initiator: null,
        date: null,
      })
    );
    scrollTopList();
  };

  const onSelectInitiator = (selectedKeysValue: string, node: any) => {
    if ("children" in node) return;
    updateFilters((prev) => ({
      ...prev,
      page: 1,
      initiator: node,
    }));
    scrollTopList();
  };

  const onChangeInitiator = () => {
    if (filters.initiator) {
      updateFilters((prev) => ({
        ...prev,
        page: 1,
        initiator: null,
      }));
    }
  };

  const handleDateChange = useCallback(
    (date: Date | null): void => {
      const newDate = date?.valueOf() || null;
      updateFilters((prev) => ({ ...prev, page: 1, date: newDate }));
      scrollTopList();
    },
    [updateFilters]
  );

  const changeStatus = useCallback(
    (status: any) => {
      updateFilters((prev) => ({ ...prev, page: 1, status }));
      scrollTopList();
    },
    [updateFilters]
  );

  const resetFilters = useCallback((): void => {
    updateFilters({
      page: 1,
      rowsPerPage: 30,
      search: "",
      tab: "Active",
      status: null,
      initiator: null,
      date: null,
    });
    scrollTopList();
  }, []);

  const handlePageChange = useCallback(
    (event: ChangeEvent<unknown>, page: number): void => {
      updateFilters((prev) => ({ ...prev, page }));
      scrollTopList();
    },
    [updateFilters]
  );

  const handleStatus = (id: number, status: string) => {
    const testStatus = status === "Hidden" ? "Available" : "Hidden";
    dispatch(updateTest({ body: { id, status: testStatus } }));
    setUpdateClick(true);
  };

  const handleDelete = (id: number, mode?: string) => {
    if (!mode) {
      dispatch(deleteTest({ id }));
    } else {
      dispatch(deleteTestHard({ id }));
    }
    setDeleteClick(true);
  };

  const handleUploadReport = (id: number, name: string) => {
    dispatch(uploadTestReport({ id, name }));
  };

  const combineRequestBody = () => {
    const body: any = {
      page: filters.page,
      perPage: filters.rowsPerPage,
    };
    if (filters.search) body.search = filters.search;
    if (filters.initiator) body.initiatorId = filters.initiator.value;
    if (filters.status) body.status = filters.status.value;
    if (filters.date) {
      const originalDate = dayjs(filters.date);
      const newDate = originalDate.add(3, "hour");
      body.date = newDate.valueOf();
    }
    body.isDeleted = filters.tab === "Active" ? false : true;
    return body;
  };

  const scrollTopList = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  return {
    searchText,
    handleSearchTestText,
    filters,
    handleTabsChange,
    orgStructure,
    onSelectInitiator,
    onChangeInitiator,
    handleDateChange,
    scrollRef,
    changeStatus,
    resetFilters,
    testList,
    handlePageChange,
    pageAmount,
    handleStatus,
    handleDelete,
    handleUploadReport,
    isProccessTest,
  };
};
